/**
 * Bug Report Hook
 *
 * Provides functionality for bug reporting including:
 * - Screenshot capture using html2canvas
 * - Paste image from clipboard
 * - Form submission with mutation
 * - Console error capture
 */

import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import html2canvas from 'html2canvas'
import { toast } from 'sonner'
import { submitBugReport } from '../services/feedback.service'
import type { BugReportRequest } from '../types/feedback.types'
import type { ConsoleError } from '../types/feedback.types'

interface UseBugReportOptions {
  onSuccess?: () => void
  getConsoleErrors?: () => ConsoleError[]
}

export function useBugReport(options?: UseBugReportOptions) {
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])

  /**
   * Capture screenshot of entire page using html2canvas
   * Hides the modal temporarily to capture the page behind it
   */
  const captureScreenshot = useCallback(async () => {
    setIsCapturing(true)

    // Find and hide all modal-related elements
    // Using multiple selectors to catch different Radix/Vaul components and overlays
    const selectors = [
      '[data-radix-portal]',
      '[data-radix-dialog-overlay]',
      '[data-vaul-drawer]',
      '[data-vaul-drawer-wrapper]',
      '[role="dialog"]',
    ]

    const hiddenElements: { el: HTMLElement; styles: { visibility: string; opacity: string; pointerEvents: string } }[] = []

    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach((el) => {
        const htmlEl = el as HTMLElement
        hiddenElements.push({
          el: htmlEl,
          styles: {
            visibility: htmlEl.style.visibility,
            opacity: htmlEl.style.opacity,
            pointerEvents: htmlEl.style.pointerEvents,
          }
        })
        // Use visibility + opacity for more reliable hiding with html2canvas
        htmlEl.style.visibility = 'hidden'
        htmlEl.style.opacity = '0'
        htmlEl.style.pointerEvents = 'none'
      })
    })

    // Longer delay to ensure DOM updates are complete
    await new Promise(resolve => setTimeout(resolve, 100))

    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: window.devicePixelRatio,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        // Ignore hidden elements
        ignoreElements: (element) => {
          return element.getAttribute('data-radix-portal') !== null ||
                 element.getAttribute('role') === 'dialog'
        },
      })
      const dataUrl = canvas.toDataURL('image/png')
      setScreenshot(dataUrl)
      toast.success('Screenshot captured')
    } catch (error) {
      console.error('Failed to capture screenshot:', error)
      toast.error('Failed to capture screenshot')
    } finally {
      // Restore modal elements
      hiddenElements.forEach(({ el, styles }) => {
        el.style.visibility = styles.visibility
        el.style.opacity = styles.opacity
        el.style.pointerEvents = styles.pointerEvents
      })
      setIsCapturing(false)
    }
  }, [])

  /**
   * Handle paste event to capture image from clipboard
   */
  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile()
        if (!blob) continue

        const reader = new FileReader()
        reader.onload = () => {
          setScreenshot(reader.result as string)
          toast.success('Image pasted from clipboard')
        }
        reader.onerror = () => {
          toast.error('Failed to read pasted image')
        }
        reader.readAsDataURL(blob)
        break
      }
    }
  }, [])

  /**
   * Clear screenshot
   */
  const clearScreenshot = useCallback(() => {
    setScreenshot(null)
  }, [])

  /**
   * Set files (replaces entire attachments array)
   */
  const setFiles = useCallback((newFiles: File[]) => {
    setAttachments(newFiles)
  }, [])

  /**
   * Clear all attachments
   */
  const clearAttachments = useCallback(() => {
    setAttachments([])
  }, [])

  /**
   * Convert base64 data URL to File object
   */
  const dataUrlToFile = useCallback(async (dataUrl: string, filename: string): Promise<File> => {
    const response = await fetch(dataUrl)
    const blob = await response.blob()
    return new File([blob], filename, { type: 'image/png' })
  }, [])

  /**
   * Submit bug report mutation
   */
  const submitMutation = useMutation({
    mutationFn: async (data: Omit<BugReportRequest, 'screenshot'>) => {
      // Convert screenshot base64 to File if present
      let screenshotFile: File | undefined
      if (screenshot) {
        try {
          screenshotFile = await dataUrlToFile(screenshot, 'screenshot.png')
        } catch (error) {
          console.error('Failed to convert screenshot:', error)
          toast.error('Failed to process screenshot - please try capturing again')
          throw error // Prevent submission from continuing without screenshot
        }
      }

      // Get console errors if available
      const consoleErrors = options?.getConsoleErrors?.() || []
      const consoleErrorsStr = consoleErrors.length > 0
        ? JSON.stringify(consoleErrors, null, 2)
        : undefined

      return submitBugReport({
        ...data,
        screenshot: screenshotFile,
        console_errors: consoleErrorsStr,
        attachments,
      })
    },
    onSuccess: (response) => {
      toast.success(response.message || 'Bug report submitted successfully')
      if (response.issue_url) {
        toast.info('GitHub issue created', {
          description: `Issue #${response.issue_number}`,
          action: {
            label: 'View',
            onClick: () => window.open(response.issue_url!, '_blank'),
          },
        })
      }
      clearScreenshot()
      clearAttachments()
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      console.error('Failed to submit bug report:', error)
      toast.error('Failed to submit bug report', {
        description: error.message,
      })
    },
  })

  return {
    screenshot,
    isCapturing,
    captureScreenshot,
    handlePaste,
    clearScreenshot,
    attachments,
    setFiles,
    clearAttachments,
    submitMutation,
  }
}
